import React, { useEffect, useState, useCallback } from "react";
import {
  Modal,
  Descriptions,
  Table,
  Tag,
  Divider,
  Space,
  Button,
  Typography,
  Dropdown,
  message,
} from "antd";

import dayjs from "dayjs";
import { PrinterOutlined } from "@ant-design/icons";
import { FrontDeskBooking, Offer } from "../../../types";
import { calculateDuration } from "../../../utils/duration";
import { displayConfirmationNumber } from "../../../utils/groupReference";
import {
  frontDeskService,
  adminLaundryService,
  reservationPrintService,
} from "../../../services/adminService";
import { PrintReceiptModal, PrintCustomerDetails } from "../../common/PrintReceiptModal";
import FolioView from "./FolioView";
import { hasPermission } from '../../../utils/permissions';

const { Text } = Typography;

interface ViewBookingModalProps {
  open: boolean;
  onCancel: () => void;
  booking:
    | (FrontDeskBooking & {
        groupChildren?: FrontDeskBooking[];
        is_group_parent?: boolean;
      })
    | null;
  roomTypes: any[];
  offers: Offer[];
  onEdit?: (booking: FrontDeskBooking) => void;
  onDelete?: (booking: FrontDeskBooking) => void;
  onPrintReceipt?: (id: string, payload: any) => void;
  onRoomCheck?: (booking: FrontDeskBooking) => void;
  onAllocate?: (booking: FrontDeskBooking) => void;
  onCheckIn?: (booking: FrontDeskBooking) => void;
}

// Backend DELETE /bookings/:id requires bookings:delete.
const canDeleteBookings = (): boolean => hasPermission('bookings:delete');

export const ViewBookingModal: React.FC<ViewBookingModalProps> = ({
  open,
  onCancel,
  booking,
  roomTypes,
  offers,
  onEdit,
  onDelete,
  onPrintReceipt,
  onRoomCheck,
  onAllocate,
  onCheckIn,
}) => {
  const [finesBreakdown, setFinesBreakdown] = useState<
    { roomNo: string; inspId: string; amount: number }[]
  >([]);
  const [laundryBreakdown, setLaundryBreakdown] = useState<
    { roomNo: string; orderNo: string; amount: number; service_type: string }[]
  >([]);
  const [printLoading, setPrintLoading] = useState(false);
  const [printReceiptOpen, setPrintReceiptOpen] = useState(false);

  const handleReservationPrint = useCallback(async (type: string) => {
    if (!booking) return;
    setPrintLoading(true);
    try {
      const bookingId = booking.id.split("-child-")[0];
      const isConfirmation = type === "confirmation";
      let blob: Blob;
      if (isConfirmation) {
        blob = await reservationPrintService.getConfirmationPdf(bookingId);
      } else {
        blob = await reservationPrintService.getRegistrationPdf(bookingId);
      }

      // Opens in a browser tab rather than saving to disk — the backend
      // already serves these with Content-Disposition: inline.
      const label = isConfirmation ? "Reservation Confirmation" : "Guest Registration Form";
      const opened = reservationPrintService.openPdf(
        blob,
        `${label} — ${booking.confirmation_number ? displayConfirmationNumber(booking.confirmation_number) : bookingId}`,
      );
      if (!opened) {
        message.error("Pop-up blocked — allow pop-ups for this site to open documents in a new tab");
        return;
      }
      message.success(`${label} opened in a new tab`);
    } catch (err) {
      message.error("Failed to print");
    } finally {
      setPrintLoading(false);
    }
  }, [booking]);

  useEffect(() => {
    const fetchFines = async () => {
      if (!open || !booking) return;
      try {
        const breakdown: { roomNo: string; inspId: string; amount: number }[] =
          [];
        if (booking.is_group_parent && booking.groupChildren) {
          for (const child of booking.groupChildren) {
            const realId = child.id.split("-child-")[0];
            const res: any = await frontDeskService.getBookingFines(realId);
            if (res.data && res.data.length > 0) {
              const amount = res.data.reduce(
                (sum: number, f: any) => sum + Number(f.amount),
                0,
              );
              if (amount > 0) {
                breakdown.push({
                  roomNo: child.room_number || "N/A",
                  inspId: child.inspection_number || "N/A",
                  amount,
                });
              }
            }
          }
        } else {
          const realId = booking.id.split("-child-")[0];
          const res: any = await frontDeskService.getBookingFines(realId);
          if (res.data && res.data.length > 0) {
            const amount = res.data.reduce(
              (sum: number, f: any) => sum + Number(f.amount),
              0,
            );
            if (amount > 0) {
              breakdown.push({
                roomNo: booking.room_number || "N/A",
                inspId: booking.inspection_number || "N/A",
                amount,
              });
            }
          }
        }
        setFinesBreakdown(breakdown);

        // Fetch laundry orders per room
        const laundryRows: {
          roomNo: string;
          orderNo: string;
          amount: number;
          service_type: string;
        }[] = [];
        const bookingsToCheck =
          booking.is_group_parent && booking.groupChildren
            ? booking.groupChildren
            : [booking];
        for (const b of bookingsToCheck) {
          try {
            const realId = b.id.split("-child-")[0];
            const lRes: any =
              await adminLaundryService.getOrdersByBooking(realId);
            const orders: any[] = Array.isArray(lRes.data) ? lRes.data : [];
            for (const order of orders) {
              if (order.status === "cancelled") continue;
              laundryRows.push({
                roomNo: b.room_number || "N/A",
                orderNo: order.order_number,
                amount: Number(order.total_amount || 0),
                service_type: order.service_type || 'laundry',
              });
            }
          } catch (_) {}
        }
        setLaundryBreakdown(laundryRows);
      } catch (err) {
        console.error("Failed to fetch fines for details view", err);
      }
    };
    fetchFines();
  }, [open, booking]);

  if (!booking) return null;

  const isGroup =
    booking.is_group_parent && booking.groupChildren && booking.groupChildren.length > 0;
  const rooms = isGroup ? booking.groupChildren! : [booking];

  // Rooms of a group can have different stay dates (a single room may have
  // been extended). The header therefore shows the group range — earliest
  // check-in → latest check-out — while every room keeps its own dates.
  const earliestCheckIn = rooms.reduce<dayjs.Dayjs | null>(
    (earliest, r) => {
      const d = dayjs(r.check_in_date);
      return d.isValid() && (!earliest || d.isBefore(earliest)) ? d : earliest;
    },
    null,
  );
  const latestCheckOut = rooms.reduce<dayjs.Dayjs | null>(
    (latest, r) => {
      const d = dayjs(r.check_out_date);
      return d.isValid() && (!latest || d.isAfter(latest)) ? d : latest;
    },
    null,
  );
  const displayCheckIn =
    isGroup && earliestCheckIn ? earliestCheckIn : dayjs(booking.check_in_date);
  const displayCheckOut =
    isGroup && latestCheckOut ? latestCheckOut : dayjs(booking.check_out_date);

  const duration = calculateDuration(displayCheckIn, displayCheckOut);

  // Nights of ONE room, from that room's own dates: extending one room must
  // not change the nights (or price) shown for the other rooms.
  const roomNights = (r: any) =>
    Math.max(
      1,
      dayjs(r.check_out_date || booking.check_out_date).diff(
        dayjs(r.check_in_date || booking.check_in_date),
        "days",
      ),
    );

  const roomCols = [
    {
      title: "Room",
      dataIndex: "room_number",
      key: "room_number",
      render: (v: string) => v || "Not Allocated",
    },
    {
      title: "Type",
      dataIndex: "room_type_name",
      key: "room_type_name",
      render: (v: string, record: FrontDeskBooking) => {
        let suffix = "";
        if (record.ac_status === "ac") suffix = " (AC)";
        if (record.ac_status === "non_ac") suffix = " (Non-AC)";
        return v ? `${v}${suffix}` : "";
      },
    },
    {
      title: "Guests",
      dataIndex: "number_of_guests",
      key: "number_of_guests",
    },
    {
      title: "Extra Beds",
      dataIndex: "extra_beds",
      key: "extra_beds",
      render: (v: number) => v || 0,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v: string) => {
        const color =
          v === "checked_in"
            ? "green"
            : v === "checked_out"
              ? "blue"
              : v === "confirmed"
                ? "cyan"
                : v === "cancelled"
                  ? "red"
                  : v === "reserved"
                    ? "purple"
                    : v === "blocked"
                      ? "volcano"
                      : v === "no_show"
                        ? "magenta"
                        : "orange";
        return <Tag color={color}>{v}</Tag>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, r: FrontDeskBooking) => {
        const isAllocated = !!r.room_id || !!r.room_number;
        return (
          <Space size="small" wrap>
            {onAllocate &&
              r.status !== "cancelled" &&
              r.status !== "checked_out" &&
              r.status !== "blocked" &&
              r.status !== "no_show" && (
                <Button
                  size="small"
                  style={
                    isAllocated
                      ? {
                          backgroundColor: "#52c41a",
                          color: "#fff",
                          borderColor: "#52c41a",
                        }
                      : {}
                  }
                  onClick={() => {
                    onCancel();
                    onAllocate(r);
                  }}
                >
                  {isAllocated ? "Allocated" : "Allocate"}
                </Button>
              )}
            {isAllocated &&
              onCheckIn &&
              (r.status === "confirmed" ||
                r.status === "pending" ||
                r.status === "reserved") && (
                <Button
                  size="small"
                  type="primary"
                  onClick={() => {
                    onCancel();
                    onCheckIn(r);
                  }}
                >
                  Check-In
                </Button>
              )}
            {onRoomCheck && (
              <Button
                size="small"
                style={
                  r.inspection_number
                    ? {
                        backgroundColor: "#52c41a",
                        color: "#fff",
                        borderColor: "#52c41a",
                      }
                    : {}
                }
                onClick={() => {
                  onCancel();
                  onRoomCheck(r);
                }}
              >
                Room Check
              </Button>
            )}
            {onDelete && canDeleteBookings() && (
              <Button
                size="small"
                danger
                onClick={() => {
                  onCancel();
                  onDelete(r);
                }}
              >
                Delete
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  const nights = booking
    ? Math.max(
        dayjs(booking.check_out_date).diff(
          dayjs(booking.check_in_date),
          "days",
        ),
        1,
      )
    : 0;

  const getActiveOffer = (
    roomTypeId: string,
    date: Date,
    acStatus?: string,
  ): Offer | null => {
    const activeOffersForRoom = offers.filter(
      (o) =>
        o.room_type_id === roomTypeId &&
        o.is_active &&
        new Date(o.start_date) <= date &&
        new Date(o.end_date) >= date &&
        !o.promo_code,
    );

    if (acStatus) {
      const exactMatch = activeOffersForRoom.find(
        (o) => o.ac_status === acStatus,
      );
      if (exactMatch) return exactMatch;
    }

    return (
      activeOffersForRoom.find((o) => !o.ac_status || o.ac_status === "any") ||
      null
    );
  };

  let extraBedsTotal = 0;
  let totalOriginalRoomPrice = 0;
  let earlyCheckInFee = 0;
  let lateCheckOutFee = 0;
  let serviceCharge = 0;
  let manualDiscountDeduction = 0;
  let taxValue = 0;
  let finalTotal = 0;
  let roomSubtotal = 0;
  let vatRate = 0;
  let discountPercentage = 0;

  const roomBreakdowns: Array<{
    roomTypeName: string;
    quantity: number;
    originalPrice: number;
    discountedPrice: number;
    offerTitle: string | null;
    nights: number;
    roomNumbers: string[];
  }> = [];

  rooms.forEach((r) => {
    const roomType = roomTypes.find((rt) => rt.id === r.room_type_id);
    let rRoomTotal = 0;
    let rExtraBedsTotal = 0;

    if (roomType) {
      let originalPrice = Number(roomType.base_price || 0);
      if (roomType.ac_status_preference === "both") {
        if (r.ac_status === "ac")
          originalPrice = Number(roomType.ac_base_price || roomType.base_price);
        else if (r.ac_status === "non_ac")
          originalPrice = Number(
            roomType.non_ac_base_price || roomType.base_price,
          );
      }
      let basePricePerNight = originalPrice;
      let offerTitle: string | null = null;

      if (
        (r as any).original_room_price !== undefined &&
        (r as any).original_room_price !== null
      ) {
        originalPrice = Number((r as any).original_room_price);
        basePricePerNight = Number((r as any).discounted_price);
        offerTitle = (r as any).offer_title || null;
      } else {
        const activeOffer = getActiveOffer(
          r.room_type_id,
          new Date(booking.check_in_date),
          r.ac_status,
        );
        if (activeOffer) {
          if (activeOffer.discount_amount) {
            basePricePerNight = Math.max(
              0,
              basePricePerNight - Number(activeOffer.discount_amount),
            );
          } else if (activeOffer.discount_percentage) {
            basePricePerNight = Math.round(
              basePricePerNight *
                (1 - Number(activeOffer.discount_percentage) / 100),
            );
          }
          offerTitle = activeOffer.title;
        }
      }

      const quantity = 1;
      const rNights = roomNights(r);

      totalOriginalRoomPrice += originalPrice * quantity * rNights;
      rRoomTotal = basePricePerNight * quantity * rNights;

      const baseTypeName = roomType?.name || r.room_type_name || "Unknown";
      let suffix = "";
      if (r.ac_status === "ac") suffix = " (AC)";
      if (r.ac_status === "non_ac") suffix = " (Non-AC)";
      const roomTypeName = `${baseTypeName}${suffix}`;

      const roomNo = r.room_number || "Not Allocated";

      const existingBreakdown = roomBreakdowns.find(
        (rb) =>
          rb.roomTypeName === roomTypeName &&
          rb.discountedPrice === basePricePerNight &&
          rb.offerTitle === offerTitle &&
          rb.nights === rNights,
      );

      if (existingBreakdown) {
        existingBreakdown.quantity += 1;
        existingBreakdown.roomNumbers.push(roomNo);
      } else {
        roomBreakdowns.push({
          roomTypeName,
          quantity,
          originalPrice,
          discountedPrice: basePricePerNight,
          offerTitle,
          nights: rNights,
          roomNumbers: [roomNo],
        });
      }

      if (r.extra_beds) {
        rExtraBedsTotal =
          Number(roomType.extra_bed_price || 0) * Number(r.extra_beds) * rNights;
        extraBedsTotal += rExtraBedsTotal;
      }
    }

    if (
      (r as any).room_subtotal !== undefined &&
      (r as any).room_subtotal !== null
    ) {
      roomSubtotal += Number((r as any).room_subtotal);
    } else {
      roomSubtotal += rRoomTotal + rExtraBedsTotal;
    }

    if ((r as any).tax_amount !== undefined && (r as any).tax_amount !== null) {
      taxValue += Number((r as any).tax_amount);
    }

    if (vatRate === 0 && Number(r.vat_rate) > 0) {
      vatRate = Number(r.vat_rate);
    }
  });

  earlyCheckInFee = Number((booking as any).early_check_in_fee || 0);
  lateCheckOutFee = Number((booking as any).late_check_out_fee || 0);
  serviceCharge = Number((booking as any).service_charge || 0);
  const serviceChargePercentage = Number(
    (booking as any).service_charge_percentage || 0,
  );
  manualDiscountDeduction = Number((booking as any).discount_amount || 0);
  discountPercentage = Number((booking as any).discount_percentage || 0);

  const pkgCharge = isGroup
    ? rooms.reduce((sum, r) => sum + Number((r as any).package_charge || 0), 0)
    : Number((booking as any).package_charge || 0);

  const total1 = roomSubtotal + pkgCharge + earlyCheckInFee + lateCheckOutFee;

  if (manualDiscountDeduction === 0 && discountPercentage > 0) {
    manualDiscountDeduction = Math.round((total1 * discountPercentage) / 100);
  }

  const total2 = Math.max(0, total1 - manualDiscountDeduction);

  if (serviceCharge === 0 && serviceChargePercentage > 0) {
    serviceCharge = Math.round((total2 * serviceChargePercentage) / 100);
  }

  const total3 = total2 + serviceCharge;

  if (taxValue === 0 && vatRate > 0) {
    taxValue = Math.round(total3 * (vatRate / 100));
  }

  finalTotal = total3 + taxValue;

  const buildComputedReceiptPayload = () => {
    const lineItems = roomBreakdowns.map((rb, idx) => ({
      productCode: `ROOM-0${idx + 1}`,
      description: `Room stay (Room ${rb.roomNumbers.join(', ')} - ${rb.roomTypeName})`,
      qty: rb.nights * rb.quantity,
      price: rb.discountedPrice,
      total: rb.discountedPrice * rb.quantity * rb.nights,
    }));

    if (extraBedsTotal > 0) {
      lineItems.push({
        productCode: "FEE-EXT",
        description: "Extra Beds Charges",
        qty: 1,
        price: extraBedsTotal,
        total: extraBedsTotal,
      });
    }

    if (earlyCheckInFee > 0) {
      lineItems.push({
        productCode: "FEE-ECI",
        description: "Early Check-In Fee",
        qty: 1,
        price: earlyCheckInFee,
        total: earlyCheckInFee,
      });
    }

    if (lateCheckOutFee > 0) {
      lineItems.push({
        productCode: "FEE-LCO",
        description: "Late Check-Out Fee",
        qty: 1,
        price: lateCheckOutFee,
        total: lateCheckOutFee,
      });
    }

    if (serviceCharge > 0) {
      lineItems.push({
        productCode: "FEE-SVC",
        description: "Service charge",
        qty: 1,
        price: serviceCharge,
        total: serviceCharge,
      });
    }

    if (pkgCharge > 0) {
      lineItems.push({
        productCode: "PKG-01",
        description: `Room Package (${(booking as any).package_name || "Package"})`,
        qty: isGroup ? rooms.length : 1,
        price: isGroup ? pkgCharge / rooms.length : pkgCharge,
        total: pkgCharge,
      });
    }

    if (finesBreakdown.length > 0) {
      finesBreakdown.forEach((f, idx) => {
        lineItems.push({
          productCode: `FINE-0${idx + 1}`,
          description: `Room Check Fine (Room ${f.roomNo} - ${f.inspId})`,
          qty: 1,
          price: f.amount,
          total: f.amount,
        });
      });
    }

    if (laundryBreakdown.length > 0) {
      laundryBreakdown.forEach((l, idx) => {
        lineItems.push({
          productCode: `LDRY-0${idx + 1}`,
          description: `Laundry Order (Room ${l.roomNo} - ${l.orderNo})`,
          qty: 1,
          price: l.amount,
          total: l.amount,
        });
      });
    }

    const finesTotal = finesBreakdown.reduce((sum, f) => sum + f.amount, 0);
    const laundryTotal = laundryBreakdown.reduce((sum, l) => sum + l.amount, 0);

    return {
      invoiceNumber: booking.confirmation_number || booking.invoice_number || `INV-${booking.id.slice(0, 8).toUpperCase()}`,
      invoiceDateTime: new Date().toISOString(),
      bookingDate: booking.created_at,
      roomNo: isGroup ? "Multiple" : booking.room_number || "Not Allocated",
      roomType: isGroup ? "Multiple" : booking.room_type_name,
      numberOfGuests: isGroup ? rooms.reduce((sum, r) => sum + (r.number_of_guests || 0), 0) : booking.number_of_guests,
      checkInDate: booking.check_in_date,
      checkOutDate: booking.check_out_date,
      numberOfNights: nights,
      customerName: booking.guest_name,
      addressLines: [booking.address_line_1, booking.address_line_2].filter(Boolean),
      phone: booking.phone,
      email: booking.email,
      nationality: "Sri Lanka",
      passportOrNic: booking.id_document_number || "",
      paymentMethod: booking.payment_method || "N/A",
      referenceNo: booking.confirmation_number || "",
      depositAmountPaid: Number(booking.deposit_paid || 0),
      subtotal: lineItems.reduce((sum, item) => sum + item.total, 0),
      discountAmount: manualDiscountDeduction,
      serviceChargePercent: discountPercentage > 0 ? discountPercentage : 0,
      serviceChargeAmount: taxValue,
      isVatInvoice: vatRate > 0,
      vatRatePercent: vatRate,
      grossTotal: finalTotal + finesTotal + laundryTotal,
      balanceDue: Math.max(0, finalTotal + finesTotal + laundryTotal - Number(booking.deposit_paid || 0)),
      lineItems: lineItems,
      isQuotation: false,
    };
  };

  const buildReceiptPayload = async () => {
    const computed = buildComputedReceiptPayload();
    const statuses = rooms.map((r: any) => r.status);
    const hasFolio = statuses.some(
      (s: string) => s === "checked_in" || s === "checked_out",
    );
    const groupId = (booking as any).group_id;
    const breakdownId =
      isGroup && groupId ? `group-${groupId}` : isGroup ? null : booking.id;

    if (hasFolio && breakdownId) {
      try {
        const res: any = await frontDeskService.getInvoiceBreakdown(breakdownId);
        const bd = res?.data;
        if (bd && Array.isArray(bd.lineItems) && bd.lineItems.length > 0) {
          const lineItems = bd.lineItems.map((item: any, idx: number) => ({
            productCode: `ITM-${String(idx + 1).padStart(2, "0")}`,
            description: item.item_label,
            qty: Number(item.quantity),
            price: Number(item.unit_price),
            total: Number(item.line_total),
          }));
          return {
            ...computed,
            lineItems,
            subtotal: Number(bd.subtotal),
            discountAmount: Number(bd.discountAmount),
            serviceChargeAmount: Number(bd.taxAmount),
            isVatInvoice: vatRate > 0,
            vatRatePercent: vatRate,
            grossTotal: Number(bd.grossTotal),
            depositAmountPaid: Number(bd.paidToDate),
            balanceDue: Math.max(0, Number(bd.balanceDue)),
          };
        }
      } catch (err) {
        console.warn("Folio invoice breakdown unavailable:", err);
      }
    }
    return computed;
  };

  const applyTaxCustomer = (payload: any, customer?: PrintCustomerDetails) => {
    if (!customer) return payload;
    payload.customerName = customer.customerName;
    if (customer.companyName) payload.companyName = customer.companyName;
    if (customer.tinVatNumber) payload.tinVatNumber = customer.tinVatNumber;
    if (customer.phone) payload.phone = customer.phone;
    if (customer.email) payload.email = customer.email;
    if (customer.address) payload.addressLines = [customer.address];
    return payload;
  };

  return (
    <Modal
      open={open}
      title={isGroup ? "Group Booking Details" : "Booking Details"}
      onCancel={onCancel}
      width={1200}
      zIndex={1050}
      footer={
        <div
          style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}
        >
          {booking && (
            <Dropdown
              menu={{
                items: [
                  { key: "confirmation", label: "Print Confirmation", onClick: () => handleReservationPrint("confirmation") },
                  { key: "registration", label: "Print Registration Form", onClick: () => handleReservationPrint("registration") },
                ],
              }}
              trigger={["click"]}
            >
              <Button icon={<PrinterOutlined />} loading={printLoading}>
                Print Documents
              </Button>
            </Dropdown>
          )}

          {onPrintReceipt &&
            booking &&
            ((!isGroup &&
              (booking.status === "checked_in" ||
                booking.status === "checked_out")) ||
              (isGroup &&
                booking.groupChildren &&
                booking.groupChildren.some(
                  (c: any) =>
                    c.status === "checked_in" || c.status === "checked_out",
                ))) && (
              <Button
                type="dashed"
                style={{
                  borderColor: "#1890ff",
                  color: "#1890ff",
                }}
                onClick={() => setPrintReceiptOpen(true)}
              >
                🖨️ Print Receipt
              </Button>
            )}

          {onRoomCheck && booking && !isGroup && (
            <Button
              style={
                booking.inspection_number
                  ? {
                      backgroundColor: "#52c41a",
                      color: "#fff",
                      borderColor: "#52c41a",
                    }
                  : {}
              }
              onClick={() => {
                onCancel();
                onRoomCheck(booking);
              }}
            >
              Room Check
            </Button>
          )}
          {onEdit && booking && (
            <Button
              onClick={() => {
                onCancel();
                onEdit(booking);
              }}
            >
              Edit
            </Button>
          )}
          {onDelete && booking && !isGroup && canDeleteBookings() && (
            <Button
              danger
              onClick={() => {
                onCancel();
                onDelete(booking);
              }}
            >
              Delete
            </Button>
          )}
          <Button onClick={onCancel} type="primary">
            Close
          </Button>
        </div>
      }
    >
      <Descriptions
        bordered
        size="small"
        column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}
      >
        <Descriptions.Item label="Guest Name">
          <strong>{booking.guest_name}</strong>
        </Descriptions.Item>
        <Descriptions.Item label="Contact Info">
          {booking.email && <div>{booking.email}</div>}
          {booking.phone && <div>{booking.phone}</div>}
        </Descriptions.Item>
        {(booking.address_line_1 || booking.address_line_2) && (
          <Descriptions.Item label="Address">
            {booking.address_line_1 && <div>{booking.address_line_1}</div>}
            {booking.address_line_2 && <div>{booking.address_line_2}</div>}
          </Descriptions.Item>
        )}
        <Descriptions.Item label="Check-In">
          {displayCheckIn.format("MMM D, YYYY")}
        </Descriptions.Item>
        <Descriptions.Item label="Check-Out">
          {displayCheckOut.format("MMM D, YYYY")}
        </Descriptions.Item>
        <Descriptions.Item label="Duration">{duration}</Descriptions.Item>
        <Descriptions.Item label="Source">
          <Tag
            color={
              {
                walk_in: "gold",
                direct: "green",
                website: "blue",
                booking_com: "orange",
                agoda: "red",
                expedia: "cyan",
                travel_agent: "purple",
                phone: "magenta",
                whatsapp: "lime",
                other: "default",
              }[booking.reservation_source] || "default"
            }
          >
            {
              {
                walk_in: "Walk-In",
                direct: "Direct",
                website: "Website",
                booking_com: "Booking.com",
                agoda: "Agoda",
                expedia: "Expedia",
                travel_agent: "Travel Agent",
                phone: "Phone",
                whatsapp: "WhatsApp",
                other: "Other",
              }[booking.reservation_source] || booking.reservation_source
            }
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Package">
          {booking.package_name || booking.package_code ? (
            <>
              <Tag color="cyan">
                {booking.package_name || booking.package_code}
              </Tag>
            </>
          ) : (
            <span style={{ color: "#999" }}>—</span>
          )}
        </Descriptions.Item>
        {booking.id_document_type && (
          <Descriptions.Item label="ID Document">
            {booking.id_document_type}: {booking.id_document_number}
          </Descriptions.Item>
        )}
        <Descriptions.Item label="Confirmation Number">
          {booking.confirmation_number
            ? displayConfirmationNumber(booking.confirmation_number)
            : ""}
        </Descriptions.Item>
        {(booking as any).late_arrival && (
          <Descriptions.Item label="Late Arrival">
            <Tag color="orange">Yes</Tag>
          </Descriptions.Item>
        )}
        {(booking as any).airport_pickup && (
          <Descriptions.Item label="Airport Pickup">
            <Tag color="blue">
              {(booking as any).airport_pickup_time
                ? (() => {
                    const [h, m] = String((booking as any).airport_pickup_time).split(":").map(Number);
                    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
                    return `${String(h12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
                  })()
                : (booking as any).airport_pickup_datetime
                  ? dayjs((booking as any).airport_pickup_datetime).format("hh:mm A")
                  : "Yes"}
            </Tag>
          </Descriptions.Item>
        )}
        {booking.special_requests && (
          <Descriptions.Item label="Special Requests" span={2}>
            {booking.special_requests}
          </Descriptions.Item>
        )}
        {booking.inspection_number && (
          <Descriptions.Item label="Inspection ID">
            <Text type="danger" strong>
              {booking.inspection_number}
            </Text>
          </Descriptions.Item>
        )}
      </Descriptions>

      <Divider>Rooms in this Booking</Divider>
      <Table
        rowKey="id"
        dataSource={rooms}
        columns={roomCols}
        pagination={false}
        size="small"
      />

      <div
        style={{
          marginTop: "24px",
          padding: "16px",
          backgroundColor: "#f5f5f5",
          borderRadius: "8px",
        }}
      >
        <h3 style={{ margin: "0 0 16px 0", fontSize: "16px" }}>
          Price Breakdown
        </h3>
        {roomBreakdowns.map((rb, idx) => (
          <div
            key={idx}
            style={{
              marginBottom: "12px",
              padding: "10px 12px",
              backgroundColor: "#fff",
              borderRadius: "6px",
              border: "1px solid #e8e8e8",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "6px",
              }}
            >
              <span style={{ fontWeight: 600, fontSize: "14px" }}>
                {rb.roomTypeName}
                {rb.offerTitle && (
                  <Tag
                    color="green"
                    style={{
                      marginLeft: "8px",
                      fontSize: "11px",
                    }}
                  >
                    {rb.offerTitle}
                  </Tag>
                )}
              </span>
              <span style={{ fontWeight: 600, fontSize: "14px" }}>
                LKR{" "}
                {(rb.discountedPrice * rb.quantity * rb.nights).toLocaleString(
                  "en-US",
                  { minimumFractionDigits: 2 },
                )}
              </span>
            </div>
            <div
              style={{
                fontSize: "12px",
                color: "#888",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>
                {rb.offerTitle ? (
                  <>
                    <span
                      style={{
                        textDecoration: "line-through",
                        color: "#bbb",
                        marginRight: "4px",
                      }}
                    >
                      LKR{" "}
                      {rb.originalPrice.toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                      })}
                    </span>
                    <span style={{ color: "#389e0d", fontWeight: 500 }}>
                      LKR{" "}
                      {rb.discountedPrice.toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                      })}
                    </span>
                    {" / night"}
                  </>
                ) : (
                  <>
                    LKR{" "}
                    {rb.discountedPrice.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                    })}
                    {" / night"}
                  </>
                )}
              </span>
              <span style={{ color: "#666" }}>
                {rb.quantity} {rb.quantity === 1 ? "room" : "rooms"} ×{" "}
                {rb.nights} {rb.nights === 1 ? "night" : "nights"}
              </span>
            </div>
          </div>
        ))}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "8px",
          }}
        >
          <span>Room Subtotal</span>
          <span>
            LKR{" "}
            {/* Stored room_subtotal (extra beds broken out on the row below),
                so the subtotal and the total are always on the same basis. */}
            {(roomSubtotal - extraBedsTotal).toLocaleString("en-US", {
              minimumFractionDigits: 2,
            })}
          </span>
        </div>
        {pkgCharge > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "8px",
            }}
          >
            <span>Room Package ({(booking as any).package_name || "Package"})</span>
            <span>
              LKR{" "}
              {pkgCharge.toLocaleString("en-US", {
                minimumFractionDigits: 2,
              })}
            </span>
          </div>
        )}
        {extraBedsTotal > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "8px",
            }}
          >
            <span>Extra Beds Charges</span>
            <span>
              LKR{" "}
              {extraBedsTotal.toLocaleString("en-US", {
                minimumFractionDigits: 2,
              })}
            </span>
          </div>
        )}
        {Number(earlyCheckInFee) > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "8px",
            }}
          >
            <span>Early Check-In Fee</span>
            <span>
              LKR{" "}
              {Number(earlyCheckInFee).toLocaleString("en-US", {
                minimumFractionDigits: 2,
              })}
            </span>
          </div>
        )}
        {Number(lateCheckOutFee) > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "8px",
            }}
          >
            <span>Late Check-Out Fee</span>
            <span>
              LKR{" "}
              {Number(lateCheckOutFee).toLocaleString("en-US", {
                minimumFractionDigits: 2,
              })}
            </span>
          </div>
        )}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "8px",
          }}
        >
          <span>Taxes & Fees ({vatRate}%)</span>
          <span>
            LKR{" "}
            {taxValue.toLocaleString("en-US", {
              minimumFractionDigits: 2,
            })}
          </span>
        </div>
        {manualDiscountDeduction > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "8px",
              color: "#389e0d",
            }}
          >
            <span>
              Manual Discount
              {discountPercentage > 0 && (
                <span
                  style={{ fontSize: "12px", color: "#888", marginLeft: "4px" }}
                >
                  ({discountPercentage}%)
                </span>
              )}
            </span>
            <span>
              − LKR{" "}
              {manualDiscountDeduction.toLocaleString("en-US", {
                minimumFractionDigits: 2,
              })}
            </span>
          </div>
        )}
        <Divider style={{ margin: "12px 0" }} />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontWeight: "bold",
            fontSize: "16px",
          }}
        >
          <span>Total Price</span>
          <span>
            LKR{" "}
            {(
              finalTotal +
              finesBreakdown.reduce((sum, f) => sum + f.amount, 0) +
              laundryBreakdown.reduce((sum, l) => sum + l.amount, 0)
            ).toLocaleString("en-US", {
              minimumFractionDigits: 2,
            })}
          </span>
        </div>
        {(booking as any).deposit_paid > 0 && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: "8px",
                  fontSize: "14px",
                  color: "#52c41a",
                }}
              >
                <span>Deposit Paid</span>
                <span>
                  − LKR {Number((booking as any).deposit_paid || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                </span>
              </div>
            </>
        )}
      </div>

      {/* Guest Folio Section */}
      {(booking.status === "checked_in" || booking.status === "checked_out") && (
        <div
          style={{
            marginTop: "24px",
            padding: "16px",
            backgroundColor: "#f5f5f5",
            borderRadius: "8px",
          }}
        >
          <h3 style={{ margin: "0 0 16px 0", fontSize: "16px" }}>
            Guest Folio
          </h3>
          <FolioView
            bookingId={booking.id}
            booking={booking}
            groupBookingIds={isGroup && booking.groupChildren ? booking.groupChildren.map((c: any) => c.id) : undefined}
          />
        </div>
      )}

      {/* Print receipt / tax invoice details */}
      {onPrintReceipt && (
        <PrintReceiptModal
          open={printReceiptOpen}
          title="Print Receipt"
          defaults={{
            customerName: booking.guest_name,
            phone: booking.phone || "",
            email: booking.email || "",
            address: [booking.address_line_1, booking.address_line_2].filter(Boolean).join(", "),
          }}
          onCancel={() => setPrintReceiptOpen(false)}
          onPrint={async (customer) => {
            const payload = applyTaxCustomer(await buildReceiptPayload(), customer);
            onPrintReceipt(booking.id, payload);
            setPrintReceiptOpen(false);
          }}
        />
      )}
    </Modal>
  );
};
